/* eslint-disable import/no-extraneous-dependencies */
import Input from "@Components/Input";
import { useController } from "react-hook-form";
import CustomCheckBox from "../CustomCheckBox";
import CountryCodeDropdown from "../CountryCodeDropdown";
import { UniversityDropdown } from "@Components/LeadForm/components/dropdown";
import Dropdown from "../Dropdown";
import styles from "../PersonalForm/PersonalForm.module.scss";
import CustomRadioInput from "../CustomRadioInput";
import SearchableDropdown from "../Dropdown/SearchableDropdown";
import TabButtonGroup from "../TabButtonGroup";
import DatePickerDropdown from "../MoveInDropdown";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import LabelledRadioGroup from "@Components/LabelledRadioGroup";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import { countryCodes } from "@Components/LeadFormModal/data/countryCodes";

const FormField = ({ step, control, error, value, setValue, dataTestId }: any) => {
  const { t } = useTranslationHook();
  const { isBetween } = useResponsiveness();
  const { field } = useController({
    name: step.name,
    control,
    rules: step.rules, // {required: true, minLength, 2}
  });

  // Safe string replacement helper function
  const safeReplace = (str?: string) => {
    if (!str) return "";
    return str.replace(/\//g, "_");
  };

  const isNameField = (fieldName: string) => {
    const nameFieldPatterns = [
      /user_details\/name$/,
      /emergency_details\/name$/,
      /guarantor_details\/name$/,
      /guarantor_details\/guarantor_employer_name$/,
    ];
    return nameFieldPatterns.some((pattern) => pattern.test(fieldName));
  };

  const dataTestIdField =
    (field?.name && safeReplace(field?.name)) || (step?.label && safeReplace(step?.label)) || "";

  switch (step?.type) {
    case "text": {
      const isName = isNameField(field.name);

      return (
        <Input
          {...step}
          type={step.inputType || step.type}
          label={t(step?.label)}
          name={field.name}
          onChange={field.onChange}
          onBlur={step.onBlur}
          error={error?.message}
          showErrorMessage={Boolean(error?.message)}
          dataTestId={dataTestIdField}
          required={step.required}
          preventNumbers={isName}
          {...(isName && !step.autoComplete ? { autoComplete: "name" } : {})}
        />
      );
    }
    case "checkbox":
      return (
        <CustomCheckBox
          label={t(step.label)}
          isSelected={step.value}
          onChange={(isChecked: boolean) => setValue(step.name, isChecked)}
        />
      );
    case "number":
      return (
        <Input
          {...step}
          label={t(step.label)}
          name={field.name}
          onChange={field.onChange}
          onBlur={step.onBlur}
          error={error?.message}
          showErrorMessage={error}
          dataTestId={dataTestIdField}
        />
      );
    case "tel": {
      // Match Group Booking: type="tel" with default autocomplete="on" (not tel-national).
      // If Chrome Autofill inserts a full international number (+91...), split into the
      // sibling phone_code field when one exists.
      const handleTelChange = (raw: string) => {
        const sanitized = String(raw).replace(/[\s\-().]/g, "");
        const canSplitCountryCode =
          typeof setValue === "function" &&
          /\/phone$/.test(field.name) &&
          !field.name.includes("employer");

        if (sanitized.startsWith("+") && canSplitCountryCode) {
          const sortedCodes = [...countryCodes].sort((a, b) => b.label.length - a.label.length);
          const matchedCode = sortedCodes.find((code) => sanitized.startsWith(code.label));
          if (matchedCode) {
            const codeFieldName = field.name.replace(/\/phone$/, "/phone_code");
            setValue(codeFieldName, matchedCode.label);
            field.onChange(sanitized.slice(matchedCode.label.length));
            return;
          }
        }

        field.onChange(sanitized.replace(/\D/g, ""));
      };

      return (
        <Input
          {...step}
          label={t(step.label)}
          name={field.name}
          onChange={handleTelChange}
          onBlur={step.onBlur}
          error={error?.message}
          showErrorMessage={error}
          dataTestId={dataTestIdField}
          // Force browser-default Autofill (same as Group Booking). Do not use tel-national
          // with type="tel" — that pairing is invalid and breaks Chrome iOS suggestions.
          autoComplete="on"
        />
      );
    }
    case "dob":
      return (
        <DatePickerDropdown
          dataTestId={dataTestIdField}
          value={step.value}
          label={t(step.label)}
          type="dob"
          placeholder={t(step.placeholder)}
          onFocus={step.onFocus}
          onChange={step.onChange}
          maxYearLimit={step?.maxYearLimit}
          minYearLimit={step?.minYearLimit}
          day={step.day}
          month={step.month}
          year={step.year}
          handleClear={step.handleClear}
          isCalendarOpened={step.isCalendarOpened}
          handleSelectDate={step.handleSelectDate}
          hideDatePicker={step.hideDatePicker}
          disabled={step.disabled}
          required={step.required}
          error={error?.message}
          showPlaceholderAndLabel={step?.showPlaceholderAndLabel}
        />
      );
    case "countryCode":
      return (
        <CountryCodeDropdown
          dataTestId="Country Code"
          {...step}
          name={field.name}
          onChange={field.onChange}
          onBlur={field.onBlur}
          error={error?.message}
          showErrorMessage={error}
          required={step.required}
        />
      );
    case "uniDropdown":
      return (
        <UniversityDropdown
          {...step}
          name={field.name}
          onChange={field.onChange}
          selectedUniversity={field.value}
          setSelectedUniversity={(university: any) => {
            step.clearErrors();
            setValue(step.name, university);
          }}
          setErrors={(...args: any) => console.log(args)}
          onBlur={field.onBlur}
          error={error?.message}
          showErrorMessage={error}
          handleOptionClick={field.onChange}
          required={step.required}
          value={value}
          dataTestId="University_input"
          isBookForm
          showPlaceholderAndLabel
        />
      );
    case "dropdown":
      return (
        <Dropdown
          {...step}
          name={field.name}
          onChange={field.onChange}
          onBlur={field.onBlur}
          error={error?.message}
          showErrorMessage={error}
          handleOptionClick={field.onChange}
          value={value}
          dataTestId={dataTestIdField}
          required={step.required}
        />
      );
    case "searchableDropdown":
      return (
        <SearchableDropdown
          {...step}
          name={field.name}
          onChange={field.onChange}
          onBlur={field.onBlur}
          error={error?.message}
          showErrorMessage={error}
          dataTestId={dataTestIdField}
        />
      );
    case "tabGroup":
      return <TabButtonGroup {...step} error={error?.message} showErrorMessage={error} />;
    case "labelledTabGroup":
      return (
        <LabelledRadioGroup
          {...step}
          error={error?.message}
          showErrorMessage={error}
          shouldShowVertical={isBetween(SCREEN_SIZE.MD, SCREEN_SIZE.LG)}
        />
      );
    case "radio":
      return (
        <CustomRadioInput
          {...step}
          error={error?.message}
          showErrorMessage={error}
          dataTestId={dataTestId}
        />
      );
    case "sectionHeader":
      return (
        <div
          className={styles.sectionHeader}
          data-testid={`{bookform-${step?.sectionHeader ? safeReplace(step?.sectionHeader) : ""}`}
        >
          <div
            className={styles.sectionTitle}
            data-testid={`{bookform-${step?.label ? safeReplace(step?.label) : ""}`}
          >
            {t(step.label)}
          </div>
          <div className={styles.divider} />
        </div>
      );
    default: {
      const isName = isNameField(field.name);

      return (
        <Input
          {...step}
          label={t(step.label)}
          name={field.name}
          onChange={step.onChange || field.onChange}
          error={error?.message}
          showErrorMessage={error}
          preventNumbers={isName}
        />
      );
    }
  }
};

export default wrapperHOC(FormField, {
  componentName: "FormField-BookForm",
  showForChina: true,
});
